import React, { useMemo, useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Container, Card, Button, Modal, Form, Alert, Table, Row, Col } from "react-bootstrap";
import { Spin } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import PageTitle from "@/common/PageTitle/PageTitle";
import {
  useCanManageEmployees,
  useHasPlatformPower,
} from "@/contexts/SuperAdminIdentityContext";
import {
  useDeleteEmployeeMutation,
  useGetEmployeeAuditQuery,
  useGetEmployeeQuery,
  usePurgeEmployeeMutation,
  useRestoreEmployeeMutation,
  useSuspendEmployeeMutation,
} from "@/features/employees/employeesApi";
import { usePatchEmployeeProfileMutation } from "@/features/activity-history/activityHistoryApi";
import type { ApiEmployeeAuditEvent, ApiEmployeeDetail } from "@/features/employees/employeeTypes";
import {
  activeDepartmentSelectOptions,
  useListEmployeeDepartmentsQuery,
} from "@/features/employees/employeeDepartmentsApi";
import {
  activeRoleSelectOptions,
  useListEmployeeRolesQuery,
} from "@/features/employees/employeeRolesApi";
import {
  formatAuditDetails,
  formatAuditEventType,
  formatAuditReason,
  formatAuditActor,
} from "@/features/employees/employeeAuditFormat";
import { extractErrorMessage } from "@/features/employees/extractErrorMessage";
import { isCanonicalEmployeeId } from "@/features/employees/mapEmployeeRow";
import {
  formatLifecycleActor,
  formatLifecycleEventType,
  formatLifecycleTimestamp,
  hasEmployeeLifecycleDetail,
  resolveLifecycleReason,
} from "@/features/employees/employeeLifecycleFormat";
import styles from "./employee-detail.module.scss";
import listStyles from "./employees.module.scss";

const DELETE_CONFIRM_PHRASE = "DELETE";

function buildPurgeConfirmPhrase(employeeId: string): string {
  return `PURGE ${employeeId}`;
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 10) || "—";
  return date.toLocaleString();
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  return value.slice(0, 10) || "—";
}

function displayName(employee: ApiEmployeeDetail): string {
  const composed = `${employee.firstName || ""} ${employee.lastName || ""}`.trim();
  return employee.displayName || composed || "—";
}

function formatStatusLabel(status: string | null | undefined): string {
  if (!status) return "unknown";
  if (status === "deleted") return "Deleted";
  return status;
}

function statusClass(status: string | null | undefined): string {
  switch (status) {
    case "active":
      return styles.statusActive;
    case "suspended":
      return styles.statusSuspended;
    case "deleted":
      return styles.statusDeleted;
    default:
      return styles.statusPending;
  }
}

function formatAuditPayload(details: Record<string, unknown> | null | undefined): string {
  if (!details || Object.keys(details).length === 0) return "—";
  return JSON.stringify(details, null, 2);
}

const EmployeeDetailPage: React.FC = () => {
  const { employeeId = "" } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();
  const canManageEmployees = useCanManageEmployees();
  const canPurgeEmployees = useHasPlatformPower("purge-employees");

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetEmployeeQuery(employeeId, {
    skip: !isCanonicalEmployeeId(employeeId),
  });

  const {
    data: auditData,
    isLoading: auditLoading,
    isFetching: auditFetching,
    isError: auditError,
    refetch: refetchAudit,
  } = useGetEmployeeAuditQuery(employeeId, {
    skip: !isCanonicalEmployeeId(employeeId),
  });

  const { data: employeeRolesData } = useListEmployeeRolesQuery(undefined, {
    skip: !canManageEmployees,
  });
  const { data: employeeDepartmentsData } = useListEmployeeDepartmentsQuery(undefined, {
    skip: !canManageEmployees,
  });

  const auditEvents = auditData?.events ?? [];

  const [suspendEmployee, { isLoading: suspending }] = useSuspendEmployeeMutation();
  const [restoreEmployee, { isLoading: restoring }] = useRestoreEmployeeMutation();
  const [deleteEmployee, { isLoading: deleting }] = useDeleteEmployeeMutation();
  const [purgeEmployee, { isLoading: purging }] = usePurgeEmployeeMutation();
  const [patchEmployeeProfile, { isLoading: savingProfile }] = usePatchEmployeeProfileMutation();

  const employee = data?.employee;
  const governanceSubmitting = suspending || restoring || deleting || purging;

  const [showLifecycleModal, setShowLifecycleModal] = useState(false);
  const [showDeleteEmployeeModal, setShowDeleteEmployeeModal] = useState(false);
  const [showPurgeEmployeeModal, setShowPurgeEmployeeModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAuditEvent, setSelectedAuditEvent] = useState<ApiEmployeeAuditEvent | null>(null);
  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    displayName: "",
    jobTitle: "",
    departmentId: "",
    manager: "",
    hireDate: "",
    employeeRoleKey: "",
  });
  const [lifecycleReason, setLifecycleReason] = useState("");
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [confirmEmployeeId, setConfirmEmployeeId] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [purgeConfirmPhrase, setPurgeConfirmPhrase] = useState("");
  const [purgeReason, setPurgeReason] = useState("");

  const roleSelectOptions = useMemo(
    () =>
      activeRoleSelectOptions(
        employeeRolesData?.roles ?? [],
        editForm.employeeRoleKey,
      ),
    [employeeRolesData?.roles, editForm.employeeRoleKey],
  );

  const lifecycleMode = employee?.status === "suspended" ? "restore" : "suspend";

  const deleteEmployeeValid = useMemo(
    () =>
      confirmPhrase === DELETE_CONFIRM_PHRASE &&
      confirmEmployeeId === employeeId &&
      deleteReason.trim().length > 0,
    [confirmPhrase, confirmEmployeeId, deleteReason, employeeId],
  );

  const purgeEmployeeValid = useMemo(
    () =>
      purgeConfirmPhrase === buildPurgeConfirmPhrase(employeeId) &&
      purgeReason.trim().length > 0,
    [purgeConfirmPhrase, purgeReason, employeeId],
  );

  const closeLifecycleModal = () => {
    setShowLifecycleModal(false);
    setLifecycleReason("");
  };

  const closeDeleteEmployeeModal = () => {
    setShowDeleteEmployeeModal(false);
    setConfirmPhrase("");
    setConfirmEmployeeId("");
    setDeleteReason("");
  };

  const closePurgeEmployeeModal = () => {
    setShowPurgeEmployeeModal(false);
    setPurgeConfirmPhrase("");
    setPurgeReason("");
  };

  const openEditModal = () => {
    if (!employee) return;
    setEditForm({
      firstName: employee.firstName || "",
      lastName: employee.lastName || "",
      displayName: employee.displayName || "",
      jobTitle: employee.jobTitle || "",
      departmentId: employee.departmentId || "",
      manager: employee.manager || "",
      hireDate: employee.hireDate ? employee.hireDate.slice(0, 10) : "",
      employeeRoleKey: employee.employeeRoleKey || "",
    });
    setShowEditModal(true);
  };

  const handleEditFieldChange = (field: keyof typeof editForm, value: string) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async () => {
    try {
      await patchEmployeeProfile({
        employeeId,
        body: {
          firstName: editForm.firstName.trim(),
          lastName: editForm.lastName.trim(),
          ...(editForm.displayName.trim()
            ? { displayName: editForm.displayName.trim() }
            : {}),
          jobTitle: editForm.jobTitle.trim() || null,
          departmentId: editForm.departmentId.trim() || null,
          manager: editForm.manager.trim() || null,
          hireDate: editForm.hireDate.trim() || null,
          employeeRoleKey: editForm.employeeRoleKey,
        },
      }).unwrap();
      toast.success("Employee profile updated.");
      setShowEditModal(false);
      refetch();
      refetchAudit();
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Failed to update employee profile");
    }
  };

  const handleLifecycleAction = async () => {
    if (!employee?.auth0Sub || !lifecycleReason.trim()) return;
    try {
      if (lifecycleMode === "restore") {
        await restoreEmployee({
          auth0Sub: employee.auth0Sub,
          reason: lifecycleReason.trim(),
        }).unwrap();
        toast.success("Employee restored.");
      } else {
        await suspendEmployee({
          auth0Sub: employee.auth0Sub,
          reason: lifecycleReason.trim(),
        }).unwrap();
        toast.success("Employee suspended.");
      }
      closeLifecycleModal();
      refetch();
      refetchAudit();
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Request failed");
    }
  };

  const handleDeleteEmployee = async () => {
    if (!deleteEmployeeValid) return;
    try {
      await deleteEmployee({
        employeeId,
        confirmPhrase: DELETE_CONFIRM_PHRASE,
        confirmEmployeeId: confirmEmployeeId.trim(),
        reason: deleteReason.trim(),
      }).unwrap();
      toast.success(`Employee ${employeeId} marked as deleted.`);
      closeDeleteEmployeeModal();
      refetch();
      refetchAudit();
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Failed to delete employee");
    }
  };

  const handlePurgeEmployee = async () => {
    if (!purgeEmployeeValid) return;
    try {
      await purgeEmployee({
        employeeId,
        confirmPhrase: buildPurgeConfirmPhrase(employeeId),
        confirmEmployeeId: employeeId,
        reason: purgeReason.trim(),
      }).unwrap();
      toast.success(`Employee ${employeeId} permanently purged.`);
      closePurgeEmployeeModal();
      navigate("/employees");
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Failed to purge employee");
    }
  };

  if (!isCanonicalEmployeeId(employeeId)) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger">Invalid employee ID.</Alert>
        <Button variant="link" onClick={() => navigate("/employees")}>
          Back to Employees
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <PageTitle title="Employee Detail" />
      <Button variant="link" className={styles.backLink} onClick={() => navigate("/employees")}>
        ← Back to Employees
      </Button>

      <Card>
        <Card.Body>
          <Spin spinning={isLoading || isFetching}>
            {isError ? (
              <Alert variant="danger">
                {extractErrorMessage(error) || "Failed to load employee."}
              </Alert>
            ) : employee ? (
              <>
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                  <div>
                    <h2 className={styles.cardTitle}>{displayName(employee)}</h2>
                    <div className="text-muted">{employee.employeeId}</div>
                  </div>
                  <span className={`${styles.statusBadge} ${statusClass(employee.status)}`}>
                    {formatStatusLabel(employee.status)}
                  </span>
                </div>

                <div className={styles.profileGrid}>
                  <div className={styles.profileField}>
                    <label>Employee ID</label>
                    <div>{employee.employeeId || "—"}</div>
                  </div>
                  <div className={styles.profileField}>
                    <label>First Name</label>
                    <div>{employee.firstName || "—"}</div>
                  </div>
                  <div className={styles.profileField}>
                    <label>Last Name</label>
                    <div>{employee.lastName || "—"}</div>
                  </div>
                  <div className={styles.profileField}>
                    <label>Display Name</label>
                    <div>{employee.displayName || displayName(employee)}</div>
                  </div>
                  <div className={styles.profileField}>
                    <label>Email</label>
                    <div>{employee.emailAddress || "—"}</div>
                  </div>
                  <div className={styles.profileField}>
                    <label>Job Title</label>
                    <div>{employee.jobTitle || "—"}</div>
                  </div>
                  <div className={styles.profileField}>
                    <label>Department</label>
                    <div>{employee.departmentDisplayName || employee.department || "—"}</div>
                  </div>
                  <div className={styles.profileField}>
                    <label>Manager</label>
                    <div>{employee.manager || "—"}</div>
                  </div>
                  <div className={styles.profileField}>
                    <label>Hire Date</label>
                    <div>{formatDate(employee.hireDate)}</div>
                  </div>
                  <div className={styles.profileField}>
                    <label>Employee Role</label>
                    <div>{employee.roleDisplayName || employee.employeeRoleKey || "—"}</div>
                  </div>
                  <div className={styles.profileField}>
                    <label>Status</label>
                    <div>{employee.status || "—"}</div>
                  </div>
                  <div className={styles.profileField}>
                    <label>Auth0 Sub</label>
                    <div>{employee.auth0Sub || "—"}</div>
                  </div>
                  <div className={styles.profileField}>
                    <label>Created By</label>
                    <div>{employee.createdBy || "—"}</div>
                  </div>
                  <div className={styles.profileField}>
                    <label>Created Date</label>
                    <div>{formatDateTime(employee.createdAt)}</div>
                  </div>
                  <div className={styles.profileField}>
                    <label>Updated By</label>
                    <div>{employee.updatedBy || "—"}</div>
                  </div>
                  <div className={styles.profileField}>
                    <label>Updated Date</label>
                    <div>{formatDateTime(employee.updatedAt)}</div>
                  </div>
                </div>

                {employee && hasEmployeeLifecycleDetail(employee) && (
                  <Alert
                    variant={employee.status === "deleted" ? "danger" : "warning"}
                    className="mt-4 mb-0"
                  >
                    <strong>
                      {formatLifecycleEventType(employee.latestLifecycleEventType)}:
                    </strong>{" "}
                    {resolveLifecycleReason(employee) || "—"}
                    <div className="small mt-2 text-muted">
                      {formatLifecycleTimestamp(employee.latestLifecycleAt)}
                      {" · "}
                      {formatLifecycleActor(employee)}
                    </div>
                  </Alert>
                )}

                {canPurgeEmployees && employee.status === "deleted" && (
                  <div className={styles.actionsRow}>
                    <Button variant="danger" onClick={() => setShowPurgeEmployeeModal(true)}>
                      Purge Employee
                    </Button>
                  </div>
                )}

                {canManageEmployees && employee.status !== "deleted" && (
                  <div className={styles.actionsRow}>
                    <Button variant="outline-primary" onClick={openEditModal}>
                      Edit Employee Details
                    </Button>
                    {(employee.status === "active" || employee.status === "suspended") && (
                      <Button
                        variant={lifecycleMode === "restore" ? "primary" : "warning"}
                        onClick={() => setShowLifecycleModal(true)}
                      >
                        {lifecycleMode === "restore" ? "Restore Employee" : "Suspend Employee"}
                      </Button>
                    )}
                    {employee.status === "suspended" && (
                      <Button variant="danger" onClick={() => setShowDeleteEmployeeModal(true)}>
                        Delete Employee
                      </Button>
                    )}
                  </div>
                )}
              </>
            ) : (
              !isLoading && <Alert variant="info">Employee not found.</Alert>
            )}
          </Spin>
        </Card.Body>
      </Card>

      <Card className="mt-4">
        <Card.Body>
          <h3 className={styles.sectionTitle}>Activity / Audit History</h3>
          <p className={`text-muted ${styles.sectionHelp}`}>
            Authoritative lifecycle history for this employee — suspend, restore, delete,
            profile changes, permissions, and access areas — including who performed each action,
            when it happened, and any reason recorded.
          </p>
          <Spin spinning={auditLoading || auditFetching}>
            {auditError ? (
              <Alert variant="danger" className="mb-0">
                Failed to load audit history.
              </Alert>
            ) : auditEvents.length === 0 ? (
              <Alert variant="light" className="mb-0">
                No audit events recorded for this employee yet.
              </Alert>
            ) : (
              <Table hover responsive className={`legacy-row-height-table ${styles.auditTable}`}>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Event</th>
                    <th>Performed By</th>
                    <th>Reason</th>
                    <th>Details</th>
                    <th>IP</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {auditEvents.map((event) => (
                    <tr key={`${event.eventTimestamp}-${event.eventId}`}>
                      <td>{formatDateTime(event.eventTimestamp)}</td>
                      <td>{formatAuditEventType(event.eventType)}</td>
                      <td>{formatAuditActor(event)}</td>
                      <td>{formatAuditReason(event.details)}</td>
                      <td>{formatAuditDetails(event.details)}</td>
                      <td>{event.sourceIp || "—"}</td>
                      <td>
                        <Button
                          variant="link"
                          size="sm"
                          className={listStyles.actionLink}
                          onClick={() => setSelectedAuditEvent(event)}
                          title="View audit details"
                        >
                          👁️
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Spin>
        </Card.Body>
      </Card>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Employee Details — {employee ? displayName(employee) : employeeId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    value={editForm.firstName}
                    onChange={(e) => handleEditFieldChange("firstName", e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    value={editForm.lastName}
                    onChange={(e) => handleEditFieldChange("lastName", e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Display Name</Form.Label>
              <Form.Control
                value={editForm.displayName}
                onChange={(e) => handleEditFieldChange("displayName", e.target.value)}
              />
              <Form.Text className="text-muted">
                Leave blank to use first and last name when saving.
              </Form.Text>
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Job Title</Form.Label>
                  <Form.Control
                    value={editForm.jobTitle}
                    onChange={(e) => handleEditFieldChange("jobTitle", e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Department</Form.Label>
                  <Form.Select
                    value={editForm.departmentId}
                    onChange={(e) => handleEditFieldChange("departmentId", e.target.value)}
                  >
                    {activeDepartmentSelectOptions(
                      employeeDepartmentsData?.departments ?? [],
                      editForm.departmentId,
                    ).map((option) => (
                      <option key={option.value || "empty"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Manager</Form.Label>
                  <Form.Control
                    value={editForm.manager}
                    onChange={(e) => handleEditFieldChange("manager", e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Hire Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={editForm.hireDate}
                    onChange={(e) => handleEditFieldChange("hireDate", e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-0">
              <Form.Label>Employee Role</Form.Label>
              <Form.Select
                value={editForm.employeeRoleKey}
                onChange={(e) => handleEditFieldChange("employeeRoleKey", e.target.value)}
              >
                {roleSelectOptions.map((option) => (
                  <option key={option.value || "empty"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveProfile} disabled={savingProfile || !editForm.employeeRoleKey}>
            {savingProfile ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showLifecycleModal} onHide={closeLifecycleModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {lifecycleMode === "restore" ? "Restore Employee" : "Suspend Employee"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {employee && (
            <>
              <p>
                {lifecycleMode === "restore" ? "Restore access for " : "Suspend "}
                <strong>{displayName(employee)}</strong>?
              </p>
              <Alert variant="warning">
                {lifecycleMode === "restore"
                  ? "This will reactivate the employee's Super Admin access."
                  : "This will suspend the employee's Super Admin access until restored."}
              </Alert>
              <Form.Group>
                <Form.Label>Reason (required)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={lifecycleReason}
                  onChange={(e) => setLifecycleReason(e.target.value)}
                  placeholder="Enter reason for this action"
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeLifecycleModal}>
            Cancel
          </Button>
          <Button
            variant={lifecycleMode === "restore" ? "primary" : "danger"}
            disabled={!lifecycleReason.trim() || governanceSubmitting}
            onClick={handleLifecycleAction}
          >
            {lifecycleMode === "restore" ? "Restore Employee" : "Suspend Employee"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteEmployeeModal} onHide={closeDeleteEmployeeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Delete Employee</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {employee && (
            <>
              <Alert variant="warning">
                This marks the employee as <strong>Deleted</strong> and moves them to Hidden
                Employee Records. The governance row is retained (tombstone) with the delete
                reason. The Auth0 user account and org membership are not changed. The EMP number
                is never reused.
              </Alert>
              <p>
                Delete <strong>{displayName(employee)}</strong> ({employee.employeeId})?
              </p>
              <Form.Group className="mb-3">
                <Form.Label>
                  Type <strong>{DELETE_CONFIRM_PHRASE}</strong> to confirm
                </Form.Label>
                <Form.Control
                  value={confirmPhrase}
                  onChange={(e) => setConfirmPhrase(e.target.value)}
                  placeholder={DELETE_CONFIRM_PHRASE}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>
                  Type employee ID <strong>{employeeId}</strong> to confirm
                </Form.Label>
                <Form.Control
                  value={confirmEmployeeId}
                  onChange={(e) => setConfirmEmployeeId(e.target.value)}
                  placeholder={employeeId}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Reason (required)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="Enter reason for deleting this employee"
                />
              </Form.Group>
              <p className={`${styles.confirmHint} mt-3 mb-0`}>
                Employee must be suspended. Self-delete and last Super Admin delete are blocked by
                the API.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDeleteEmployeeModal}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!deleteEmployeeValid || governanceSubmitting}
            onClick={handleDeleteEmployee}
          >
            Delete Employee
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPurgeEmployeeModal} onHide={closePurgeEmployeeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Purge Employee</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {employee && (
            <>
              <Alert variant="danger">
                This <strong>permanently removes</strong> the employee governance record and
                deletes the Auth0 user from the Super Admin tenant (org membership + user account).
                The employee will disappear from Employees and Hidden Employee Records and will
                not reappear as a Pending Employee. Employee Detail will no longer load. One final
                audit event (<code>sa-employee-purged</code>) remains in Activity History with actor,
                reason, employeeId, email/auth0Sub, and IP/userAgent.
                The EMP number is never reused.
              </Alert>
              <p>
                Permanently purge <strong>{displayName(employee)}</strong> ({employee.employeeId})?
              </p>
              <Form.Group className="mb-3">
                <Form.Label>
                  Type <strong>{buildPurgeConfirmPhrase(employeeId)}</strong> to confirm
                </Form.Label>
                <Form.Control
                  value={purgeConfirmPhrase}
                  onChange={(e) => setPurgeConfirmPhrase(e.target.value)}
                  placeholder={buildPurgeConfirmPhrase(employeeId)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Reason (required)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={purgeReason}
                  onChange={(e) => setPurgeReason(e.target.value)}
                  placeholder="Enter reason for permanently purging this employee"
                />
              </Form.Group>
              <p className={`${styles.confirmHint} mt-3 mb-0`}>
                Employee must already be deleted (tombstone). Only Super Admins may purge.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closePurgeEmployeeModal}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!purgeEmployeeValid || governanceSubmitting}
            onClick={handlePurgeEmployee}
          >
            Purge Employee
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(selectedAuditEvent)}
        onHide={() => setSelectedAuditEvent(null)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Audit Event Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAuditEvent && (
            <>
              <Row>
                <Col md={6}>
                  <p><strong>Event:</strong> {formatAuditEventType(selectedAuditEvent.eventType)}</p>
                  <p><strong>When:</strong> {formatDateTime(selectedAuditEvent.eventTimestamp)}</p>
                  <p><strong>Performed by:</strong> {formatAuditActor(selectedAuditEvent)}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Reason:</strong> {formatAuditReason(selectedAuditEvent.details)}</p>
                  <p><strong>IP:</strong> {selectedAuditEvent.sourceIp || "—"}</p>
                  <p><strong>Status:</strong> {selectedAuditEvent.status || "—"}</p>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md={12}>
                  <h6>Details payload</h6>
                  <pre className={styles.auditPayload}>{formatAuditPayload(selectedAuditEvent.details)}</pre>
                </Col>
              </Row>
              {selectedAuditEvent.userAgent && (
                <Row className="mt-3">
                  <Col md={12}>
                    <h6>User agent</h6>
                    <p className="text-muted mb-0" style={{ fontSize: "12px", wordBreak: "break-all" }}>
                      {selectedAuditEvent.userAgent}
                    </p>
                  </Col>
                </Row>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedAuditEvent(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default withAuthenticationRequired(EmployeeDetailPage);
